import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';
import Box from '../Box/Box';
import throttle from 'lodash/throttle';
import { rem } from 'polished';
import { theme } from '../../../config/theme';
import Grid from '../Grid/Grid';

const useOutsideHandler = ({ wrapperRef, triggerRef, close }) => {
    // Close menu if clicked on outside of element
    const handleClickOutside = (e) => {
        if (
            !triggerRef.current.contains(e.target) && // if click is not on trigger
            wrapperRef.current && // if wrapper exists
            !wrapperRef.current.contains(e.target) // if click is not on wrapper
        ) {
            close();
        }
    };

    React.useEffect(() => {
        // Bind the event listener to both mouse and key events
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleClickOutside);
        return () => {
            // Unbind the event listener to clean up
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleClickOutside);
        };
    }, [wrapperRef, triggerRef]);
};

const useCloseOnScroll = (close, isOpen) => {
    const runOnScroll = () => {
        isOpen && close();
    };

    React.useEffect(() => {
        // throttle to prevent multiple calls
        window.addEventListener('scroll', throttle(runOnScroll, 1000));

        return () => {
            // Unbind the event listener to clean up
            window.removeEventListener('scroll', throttle(runOnScroll, 1000));
        };
    }, [isOpen]);
};

export const MegaMenu = ({
    renderTrigger,
    setActiveTabElement,
    setIsMegaMenuOpen,
    children,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const wrapperRef = React.useRef(null); // used to detect clicks outside of component
    const triggerRef = React.useRef(null); // used to detect clicks on trigger

    // toggle menu open/closed
    const toggle = () => {
        setIsOpen(!isOpen);
    };

    // close menu
    const close = () => {
        setIsOpen(false);
    };

    React.useEffect(() => {
        setIsMegaMenuOpen(isOpen);

        if (isOpen && triggerRef.current) {
            setActiveTabElement(triggerRef.current);
        }
    }, [isOpen]);

    useOutsideHandler({ wrapperRef, triggerRef, close }); // create bindings for closing menu from outside wrapper
    useCloseOnScroll(close, isOpen); // create bindings for closing menu on scroll

    return (
        <Box>
            {renderTrigger({ isOpen, toggle, triggerRef })}
            <AnimatePresence>
                {isOpen && (
                    <Box
                        overflowX="auto"
                        as={motion.ul}
                        backgroundColor="white"
                        ref={wrapperRef}
                        position="fixed"
                        left="0"
                        width="100%"
                        boxShadow={theme.shadows.sm}
                        marginTop="1px"
                        height={rem(348)}
                        paddingY={rem(28)}
                        zIndex={100}
                    >
                        <Grid>
                            <Box
                                gridColumn={{ _: '1/-1', xl: '2/-2' }}
                                as={motion.div}
                            >
                                {children(close)}
                            </Box>
                        </Grid>
                    </Box>
                )}
            </AnimatePresence>
        </Box>
    );
};
